/**
 * Agendamento de slide — espelho em TS da regra que a API aplica em
 * apps/api/Services/SlideSchedule.cs.
 *
 * A API é quem MANDA (ela filtra o que a TV recebe). Aqui a mesma regra roda
 * só pra dizer ao usuário, na hora da edição, o que ele acabou de configurar:
 * a frase-resumo e o selo "no ar agora". Ao mexer numa, mexa na outra —
 * slideSchedule.test.ts trava os casos de borda.
 */

export interface SlideSchedule {
  /** data inicial inclusiva, AAAA-MM-DD */
  start?: string;
  /** data final inclusiva, AAAA-MM-DD */
  end?: string;
  /** dias da semana: 0=domingo … 6=sábado. Vazio/ausente = todos */
  days?: number[];
  /** faixa de horário HH:mm (opcional) */
  from?: string;
  to?: string;
}

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAY_FULL = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** Slide sem nenhuma restrição — equivale a não ter agendamento. */
export function isEmptySchedule(s?: SlideSchedule | null): boolean {
  if (!s) return true;
  return !s.start && !s.end && !s.from && !s.to && !(s.days && s.days.length > 0);
}

export function parseSchedule(json?: string | null): SlideSchedule | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as SlideSchedule) : null;
  } catch {
    return null;
  }
}

/** Serializa pro backend. Agendamento vazio vira null (= sempre exibe). */
export function serializeSchedule(s?: SlideSchedule | null): string | null {
  if (isEmptySchedule(s)) return null;
  const out: SlideSchedule = {};
  if (s!.start) out.start = s!.start;
  if (s!.end) out.end = s!.end;
  if (s!.days?.length && s!.days.length < 7) out.days = [...s!.days].sort();
  if (s!.from && s!.to) { out.from = s!.from; out.to = s!.to; }
  return isEmptySchedule(out) ? null : JSON.stringify(out);
}

function toMinutes(hhmm?: string): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  return h >= 0 && h < 24 && min >= 0 && min < 60 ? h * 60 + min : null;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * O slide está no ar em `now`? Mesma semântica do C#: datas inclusivas, janela
 * de horário podendo cruzar a meia-noite, e agendamento vazio/inválido = no ar
 * (fail-open — agendamento quebrado nunca apaga a tela).
 */
export function isLive(s: SlideSchedule | null | undefined, now: Date = new Date()): boolean {
  if (isEmptySchedule(s)) return true;
  const sc = s!;

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const from = toMinutes(sc.from);
  const to = toMinutes(sc.to);

  // A janela de horário decide a que DIA este instante pertence: numa janela
  // 22:00→02:00 agendada pra terça, 01:00 de quarta ainda é "a terça".
  let day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (from !== null && to !== null && from !== to) {
    if (to < from) {
      if (nowMin >= from) { /* primeira metade: dia = hoje */ }
      else if (nowMin < to) day = new Date(day.getTime() - 86400000);
      else return false;
    } else if (nowMin < from || nowMin >= to) {
      return false;
    }
  }

  const dayStr = ymd(day);
  if (sc.start && dayStr < sc.start) return false;
  if (sc.end && dayStr > sc.end) return false;
  if (sc.days?.length && !sc.days.includes(day.getDay())) return false;

  return true;
}

/** 'AAAA-MM-DD' → '07/09'. Sem Date pra não escorregar de fuso. */
function brDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

function daysPhrase(days: number[]): string {
  const sorted = [...days].sort();
  if (sorted.length === 7) return '';
  if (sorted.length === 5 && [1, 2, 3, 4, 5].every(d => sorted.includes(d))) return 'de segunda a sexta';
  if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) return 'aos fins de semana';
  const names = sorted.map(d => WEEKDAY_FULL[d]);
  const last = names.pop()!;
  const list = names.length ? `${names.join(', ')} e ${last}` : last;
  return sorted.length === 1 ? `toda ${list}` : `toda ${list}`;
}

/**
 * Frase em português do que foi configurado — é ela que o usuário lê pra
 * conferir se acertou. Ex.: "Exibe toda terça e quarta, de 07/09 a 08/09".
 */
export function describeSchedule(s: SlideSchedule | null | undefined): string {
  if (isEmptySchedule(s)) return 'Exibe sempre, em todos os dias.';
  const sc = s!;
  const parts: string[] = [];

  const dias = sc.days?.length ? daysPhrase(sc.days) : '';
  parts.push(dias ? `Exibe ${dias}` : 'Exibe todos os dias');

  if (sc.start && sc.end) {
    parts.push(sc.start === sc.end ? `só em ${brDate(sc.start)}` : `de ${brDate(sc.start)} a ${brDate(sc.end)}`);
  } else if (sc.start) {
    parts.push(`a partir de ${brDate(sc.start)}`);
  } else if (sc.end) {
    parts.push(`até ${brDate(sc.end)}`);
  }

  if (sc.from && sc.to) parts.push(`das ${sc.from} às ${sc.to}`);

  return parts.join(', ') + '.';
}

export type ScheduleState =
  | { kind: 'always' }
  | { kind: 'live' }
  | { kind: 'expired'; on: string }
  | { kind: 'upcoming'; on: string }
  | { kind: 'idle' };

/** Estado pro selo colorido: no ar / começa em / expirou / fora do dia. */
export function scheduleState(s: SlideSchedule | null | undefined, now: Date = new Date()): ScheduleState {
  if (isEmptySchedule(s)) return { kind: 'always' };
  const sc = s!;
  const today = ymd(now);
  if (sc.end && today > sc.end) return { kind: 'expired', on: sc.end };
  if (sc.start && today < sc.start) return { kind: 'upcoming', on: sc.start };
  return isLive(sc, now) ? { kind: 'live' } : { kind: 'idle' };
}
